import React, { ReactElement, ReactNode } from 'react'
import styled from 'styled-components'

import Checkbox from './Checkbox'
import { InputLabel } from './Input'
import { generateRoundFlagIconFromCountryCode } from '../util/Flags'
import { ManagedPaymentMethod, getPaymentMethods } from '../util/Managed'

/**
 * Building blocks shared by the two "save an account" drawers (your own
 * accounts and a contact's), so both read as one product.
 */

export const ModalIntro = styled.div`
  background: var(--surface-subtle, #f9fafb);
  border: 1px solid var(--border-soft, #eceff3);
  border-radius: 16px;
  color: var(--text-muted, #5f6675);
  font-size: 13.5px;
  line-height: 1.55;
  padding: 13px 14px;
`

export const ModalSection = styled.div`
  background: var(--surface-subtle, #fcfcfd);
  border: 1px solid var(--border-soft, #eceff3);
  border-radius: 18px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
`

export const SectionHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`

export const SectionTitle = styled.div`
  color: var(--ink, #20242d);
  font-size: 15px;
  font-weight: 500;
`

export const SectionDescription = styled.div`
  color: var(--text-muted, #6b7280);
  font-size: 13px;
  line-height: 1.5;
`

export const FormStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

export const ResponsiveFields = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
`

export const FieldHint = styled.span`
  color: var(--text-faint, #8a919e);
  font-size: 12px;
  line-height: 1.45;
`

export const InlineNotice = styled.div`
  background: var(--notice-bg, #fff8ef);
  border: 1px solid var(--notice-border, #fde1c5);
  border-radius: 12px;
  color: var(--notice-ink, #8b5a20);
  font-size: 12.5px;
  line-height: 1.45;
  padding: 11px 12px;
`

/* Two tiles side by side where there is room (the wide modal), stacked in the
   narrower drawer so neither title wraps. */
const AccountTypeGrid = styled.div<{ $stacked?: boolean }>`
  display: grid;
  gap: 10px;
  grid-template-columns: ${({ $stacked }) => $stacked ? '1fr' : 'repeat(2, minmax(0, 1fr))'};

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`

const AccountTypeTile = styled.button<{ $active?: boolean }>`
  align-items: center;
  background: ${({ $active }) => $active ? 'var(--surface-hover, #f7f7f8)' : 'var(--surface, #ffffff)'};
  border: 1px solid ${({ $active }) => $active ? 'var(--border-strong, #d7dbe3)' : 'var(--border, #e3e7ef)'};
  border-radius: 14px;
  color: var(--ink, #20242d);
  cursor: pointer;
  display: grid;
  gap: 10px;
  grid-template-columns: auto minmax(0, 1fr) auto;
  min-height: 52px;
  padding: 9px 12px;
  text-align: left;
  transition: 0.16s ease background-color, 0.16s ease border-color;
  width: 100%;

  &:hover:not(:disabled) {
    background: ${({ $active }) => $active ? 'var(--surface-hover, #f7f7f8)' : 'var(--surface-muted, #f8fafc)'};
    border-color: var(--border-strong, #d8deea);
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`

const AccountTypeTileTitle = styled.div`
  color: var(--ink, #20242d);
  font-size: 13px;
  font-weight: 500;
  line-height: 1.25;
  white-space: nowrap;
`

const AccountTypeTileSubtitle = styled.div`
  color: var(--text-muted, #6b7280);
  font-size: 11.5px;
  line-height: 1.35;
`

const AccountTypeTileIcon = styled.div`
  align-items: center;
  display: flex;
  height: 24px;
  justify-content: flex-start;
  min-width: 48px;
`

const TileMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`

export const FlagStack = styled.div`
  align-items: center;
  display: flex;
  min-width: 0;

  > * + * {
    margin-left: -7px;
  }
`

const IconBadge = styled.div`
  align-items: center;
  background: var(--surface, #ffffff);
  border: 1px solid var(--border, #e4e8ef);
  border-radius: 999px;
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.05);
  display: flex;
  flex: 0 0 auto;
  height: 24px;
  justify-content: center;
  overflow: hidden;
  width: 24px;
`

const RailMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const RailLabel = styled.span`
  color: var(--ink, #20242d);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.3;
`

const RailHint = styled.span`
  color: var(--icon-muted, #747894);
  font-size: 12px;
  line-height: 1.35;
`

const LockedBox = styled.div`
  align-items: center;
  background: var(--surface-muted, #f8fafc);
  border: 1px dashed var(--border-strong, #d6dae2);
  border-radius: 12px;
  color: var(--ink, #20242d);
  display: flex;
  font-size: 14px;
  font-weight: 500;
  gap: 10px;
  min-height: 46px;
  padding: 0 14px;
`

const LockGlyph = styled.span`
  color: var(--icon-muted, #747894);
  display: flex;
  flex: 0 0 auto;
`

export const FlagBadge = ({ countryCode }: { countryCode: string }): ReactElement => (
  <IconBadge>{generateRoundFlagIconFromCountryCode(countryCode, 18)}</IconBadge>
)

export const MethodBadge = ({ method }: { method: ManagedPaymentMethod }): ReactElement => (
  <IconBadge>
    <img src={method.logo} style={{ height: 16, width: 'auto', maxWidth: 16 }} />
  </IconBadge>
)

/** A label, a hint and the flags of the currencies an account format moves. */
export const RailOption = ({ label, hint, flags }: { label: string, hint?: string, flags: string[] }): ReactElement => (
  <div style={{ alignItems: 'center', display: 'flex', gap: 10, minHeight: 34 }}>
    <FlagStack>
      {flags.map(flag => <FlagBadge key={flag} countryCode={flag} />)}
    </FlagStack>
    <RailMeta>
      <RailLabel>{label}</RailLabel>
      {hint && <RailHint>{hint}</RailHint>}
    </RailMeta>
  </div>
)

/** A value the user cannot change, shown where an input would be. */
interface LockedFieldProps {
  label: string
  value: ReactNode
  hint?: ReactNode
}

export const LockedField = ({ label, value, hint }: LockedFieldProps): ReactElement => (
  <FieldGroup>
    <InputLabel>{label}</InputLabel>
    <LockedBox aria-readonly='true'>
      <LockGlyph aria-hidden='true'>
        <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8'
          strokeLinecap='round' strokeLinejoin='round'>
          <rect x='4' y='11' width='16' height='10' rx='2' />
          <path d='M8 11V7a4 4 0 0 1 8 0v4' />
        </svg>
      </LockGlyph>
      <span>{value}</span>
    </LockedBox>
    {hint && <FieldHint>{hint}</FieldHint>}
  </FieldGroup>
)

/** Coins shown on the wallet tile in the current mode. */
const walletTileMethods = (testMode: boolean): ManagedPaymentMethod[] => {
  const methods = getPaymentMethods()
    .filter(method => !method.isHidden)
    .filter(method => testMode ? !!method.isTestnet : !method.isTestnet)
  return [
    methods.find(method => method.ticker === 'BTC' && !method.hostChain),
    methods.find(method => method.ticker === 'ETH'),
    methods.find(method => method.ticker === 'USDC')
  ].filter(Boolean) as ManagedPaymentMethod[]
}

export type AccountKind = 'wallet' | 'bank'

interface AccountKindTilesProps {
  value: AccountKind
  onChange: (kind: AccountKind) => void
  testMode: boolean
  disabled?: boolean
  bankDisabled?: boolean
  bankSubtitle?: string
  walletSubtitle?: string
  /** One tile per row, for the narrow drawer. */
  stacked?: boolean
}

/** Wallet or bank account, as two tiles. */
export const AccountKindTiles = (props: AccountKindTilesProps): ReactElement => (
  <AccountTypeGrid $stacked={props.stacked}>
    <AccountTypeTile
      type='button'
      disabled={props.disabled}
      $active={props.value === 'wallet'}
      onClick={() => props.onChange('wallet')}>
      <AccountTypeTileIcon>
        <FlagStack>
          {walletTileMethods(props.testMode).map(method =>
            <MethodBadge key={`${method.name}-${method.ticker}`} method={method} />)}
        </FlagStack>
      </AccountTypeTileIcon>
      <TileMeta>
        <AccountTypeTileTitle>Wallet</AccountTypeTileTitle>
        {props.walletSubtitle && <AccountTypeTileSubtitle>{props.walletSubtitle}</AccountTypeTileSubtitle>}
      </TileMeta>
      <Checkbox isRadio checked={props.value === 'wallet'} />
    </AccountTypeTile>

    <AccountTypeTile
      type='button'
      disabled={props.disabled || props.bankDisabled}
      $active={props.value === 'bank'}
      onClick={() => props.onChange('bank')}>
      <AccountTypeTileIcon>
        <FlagStack>
          <FlagBadge countryCode='US' />
          <FlagBadge countryCode='EU' />
          <FlagBadge countryCode='GB' />
        </FlagStack>
      </AccountTypeTileIcon>
      <TileMeta>
        <AccountTypeTileTitle>Bank account</AccountTypeTileTitle>
        {props.bankSubtitle && <AccountTypeTileSubtitle>{props.bankSubtitle}</AccountTypeTileSubtitle>}
      </TileMeta>
      <Checkbox isRadio checked={props.value === 'bank'} />
    </AccountTypeTile>
  </AccountTypeGrid>
)
